/**
 * payoutEngine.js — Pure payout calculation engine
 *
 * Computes per-(job, person, role) earnings independent of date windows.
 * Status triggers determine when a "pending" amount becomes "earned" (eligible
 * for the next pay period).
 *
 * Rules (locked 2026-04-23):
 *   - Originator earns: when job status reaches 'paid' AND status !== 'void' (client-paid gate removed 2026-05-21)
 *   - Production (lead + hourly) earns: when status reaches `post_production` or beyond
 *   - Post-production (lead + hourly) earns: when status reaches `delivered` or beyond
 *   - Lead remainder = max(0, dept.budget − sum(non-lead hourly earnings))
 *   - Lead's own logged hours: advisory (do NOT reduce remainder)
 *   - Rate precedence: snapshot rate wins when present, matrix fills in for un-quoted hours
 *   - Travel = mileage reimbursement, NOT hourly
 *   - Meeting / Admin / Other = company admin pool (paid via matrix `admin` rate, no project budget)
 *   - Voided jobs: prod/post earnings stay payable (work was done); originator forfeits
 *
 * See /memories/session/payout-engine-plan.md for full spec.
 */

import { computeItemsAllocation } from './calcEngine.js';

// ── Status / role / category constants ────────────────────────────────────

export const STATUS_FLOW = ['intake', 'quoted', 'confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced', 'paid', 'archived'];

export const ROLES = ['originator', 'prod_lead', 'prod_hourly', 'post_lead', 'post_hourly'];

/** Status threshold at which each role's earnings flip pending → earned. */
export const ROLE_TRIGGERS = {
  originator:   'paid',             // status-only trigger (client-paid gate removed 2026-05-21)
  prod_lead:    'post_production',
  prod_hourly:  'post_production',
  post_lead:    'delivered',
  post_hourly:  'delivered',
};

/** Time-entry category → matrix rate key. Travel handled separately as mileage. */
const CATEGORY_TO_RATE_KEY = {
  Shoot:   'production',
  Edit:    'post_production',
  Meeting: 'admin',
  Admin:   'admin',
  Other:   'admin',
};

/** Time-entry category → which project dept budget it draws from (null = none). */
const CATEGORY_TO_DEPT = {
  Shoot: 'prod',
  Edit:  'post',
  // Travel / Meeting / Admin / Other → no project dept
};

export function categoryToRateKey(cat) { return CATEGORY_TO_RATE_KEY[cat] || null; }
export function categoryToDept(cat) { return CATEGORY_TO_DEPT[cat] || null; }
export function isCategoryProjectPaid(cat) { return CATEGORY_TO_DEPT[cat] != null; }
export function isCategoryAdminPool(cat) { return CATEGORY_TO_RATE_KEY[cat] === 'admin'; }
export function isCategoryMileage(cat) { return cat === 'Travel'; }

// ── Status helpers ────────────────────────────────────────────────────────

/**
 * Is `status` at or past the `threshold` step in the FLOW?
 * `void` is never considered past anything.
 */
export function isStatusAtOrPast(status, threshold) {
  if (status === 'void') return false;
  const a = STATUS_FLOW.indexOf(status);
  const b = STATUS_FLOW.indexOf(threshold);
  return a >= 0 && b >= 0 && a >= b;
}

/** Does the role's trigger fire at the given current status? */
export function isRoleTriggered(role, status) {
  const threshold = ROLE_TRIGGERS[role];
  if (!threshold) return false;
  return isStatusAtOrPast(status, threshold);
}

/**
 * Does moving from oldStatus → newStatus cross any trigger boundary backward?
 * (i.e. earnings would be at risk of being un-earned, except they're locked).
 */
export function isBackslide(oldStatus, newStatus) {
  if (oldStatus === newStatus) return false;
  if (newStatus === 'void') return true;
  const triggers = ['post_production', 'delivered', 'paid'];
  for (const t of triggers) {
    if (isStatusAtOrPast(oldStatus, t) && !isStatusAtOrPast(newStatus, t)) return true;
  }
  return false;
}

/**
 * Filter time entries (or any row with a `date` field) to a YYYY-MM-DD inclusive
 * range. Rows without a date are excluded. Used to scope mileage/admin pool
 * aggregation to the current open pay period.
 */
export function filterEntriesByDateRange(entries, startDate, endDate) {
  if (!Array.isArray(entries) || !startDate || !endDate) return [];
  return entries.filter(e => {
    const d = typeof e?.date === 'string' ? e.date.slice(0, 10) : null;
    if (!d) return false;
    return d >= startDate && d <= endDate;
  });
}

// ── Rate lookup ───────────────────────────────────────────────────────────

/**
 * Get hourly rate for a person on a job in a category.
 * Snapshot wins when present (locked at quote time), matrix fills in for un-quoted hours.
 *
 * @param {object} job — job with optional calcSnapshot
 * @param {string} personName
 * @param {string} category — time-entry category (Shoot / Edit / etc.)
 * @param {object} settings — { data: { crewRates: { [crewId]: { production, post_production, admin } } } }
 * @param {Array} roster — roster array for crewId lookup
 * @returns {number} rate in $/hour, or 0 if nothing found
 */
export function getRateForPersonOnJob(job, personName, category, settings, roster = []) {
  if (!personName) return 0;
  const lcName = personName.toLowerCase();
  const dept = categoryToDept(category);

  // Snapshot rate (only for project-paid categories, since admin pool isn't in snapshots)
  if (dept) {
    const ns = job?.calcSnapshot;
    if (ns) {
      // Staycation crew are all mirrored into post.hourly at their plan rate
      // (segment editing), so ANY logged category resolves to that one rate —
      // a stray Shoot entry must not fall through to the crewRates matrix.
      const deptKey = ns?.payMode === 'staycation' ? 'post' : (dept === 'prod' ? 'prod' : 'post');
      // Try new shape first: ns.prod.hourly[]
      const newHourly = ns?.[deptKey]?.hourly;
      // Old shape: ns.teams.prod[].type === 'hourly'
      const oldHourly = ns?.teams?.[deptKey]?.filter ? ns.teams[deptKey].filter(m => m.type === 'hourly') : null;
      const hourly = newHourly || oldHourly || [];
      const found = hourly.find(h => (h.name || '').toLowerCase() === lcName);
      if (found) {
        const rate = parseFloat(found.hourlyRate ?? found.rate) || 0;
        if (rate > 0) return rate;
      }
    }
  }

  // Matrix fallback
  const rateKey = categoryToRateKey(category);
  if (!rateKey) return 0;
  const member = roster.find(r => (r.name || '').toLowerCase() === lcName);
  if (!member) return 0;
  const crewId = member.crewId || member.crew_id;
  if (!crewId) return 0;
  return parseFloat(settings?.data?.crewRates?.[crewId]?.[rateKey]) || 0;
}

// ── Mileage ───────────────────────────────────────────────────────────────

export function getMileageRate(settings) {
  const rate = parseFloat(settings?.data?.mileageRate);
  return Number.isFinite(rate) && rate > 0 ? rate : 0.70;
}

export function computeMileageReimbursement(miles, settings) {
  const m = parseFloat(miles) || 0;
  if (m <= 0) return 0;
  return m * getMileageRate(settings);
}

// ── Lead remainder ────────────────────────────────────────────────────────

export function computeLeadRemainder(deptBudget, hourlyTotalNonLead) {
  return Math.max(0, (parseFloat(deptBudget) || 0) - (parseFloat(hourlyTotalNonLead) || 0));
}

// ── Invoice-aware "fully paid" check ──────────────────────────────────────

/**
 * Does the sum of paid invoices on this job meet or exceed job.revenue?
 * Used for originator trigger (snapshot status can lag invoice status).
 */
export function isJobFullyPaidByClient(job, invoices) {
  const revenue = parseFloat(job?.revenue) || 0;
  if (revenue <= 0) return false;
  const jobId = job?.id;
  const paid = (invoices || [])
    // 'extra' invoices are on-top charges, not project payment — excluding them
    // prevents a paid extra from tipping the client-paid gate and firing crew
    // earnings before the actual balance is paid.
    .filter(inv => (inv.jobId === jobId || inv.job_id === jobId) && inv.status === 'paid'
      && (inv.invoiceType ?? inv.invoice_type) !== 'extra')
    .reduce((s, inv) => {
      const amt = parseFloat(inv.paidAmount ?? inv.paid_amount ?? inv.amount) || 0;
      return s + amt;
    }, 0);
  return paid >= revenue - 0.01; // float tolerance
}

// ── Job assignment normalization ──────────────────────────────────────────

/**
 * Pull originators / prod / post structure from a job's calcSnapshot.
 * Tolerates both new and old snapshot shapes.
 */
export function getJobAssignments(job) {
  const ns = job?.calcSnapshot;
  const empty = { originators: [], prod: { lead: null, hourly: [], budget: 0 }, post: { lead: null, hourly: [], budget: 0 } };
  if (!ns) return empty;

  const originators = ns.originators || [];
  // leads[]: items-mode mirrors carry every distinct lead; legacy has one
  const prod = ns.prod ? {
    lead: ns.prod.lead || null,
    leads: ns.prod.leads?.length ? ns.prod.leads : (ns.prod.lead ? [ns.prod.lead] : []),
    hourly: ns.prod.hourly || [],
    budget: parseFloat(ns.prod.budget ?? ns.amounts?.prod) || 0,
  } : { lead: null, leads: [], hourly: [], budget: parseFloat(ns?.amounts?.prod) || 0 };
  const post = ns.post ? {
    lead: ns.post.lead || null,
    leads: ns.post.leads?.length ? ns.post.leads : (ns.post.lead ? [ns.post.lead] : []),
    hourly: ns.post.hourly || [],
    budget: parseFloat(ns.post.budget ?? ns.amounts?.post) || 0,
  } : { lead: null, leads: [], hourly: [], budget: parseFloat(ns?.amounts?.post) || 0 };

  return { originators, prod, post };
}

// ── Core: compute the ledger for a job ────────────────────────────────────

/**
 * Compute what the job_earnings_ledger SHOULD contain for one job, given
 * its current snapshot, its time entries, whether it's fully paid, and rate context.
 *
 * Returns an array of:
 *   { person, crewId, role, amount, isEligibleNow, hours? }
 *
 * `isEligibleNow` indicates whether the trigger condition is currently met.
 * The caller decides how to reconcile with existing ledger rows (e.g. fire
 * pending→earned, leave already-earned rows alone).
 *
 * Rows with amount === 0 are still returned for visibility (caller may filter).
 *
 * @param {object} job
 * @param {Array}  timeEntries — entries for THIS job only
 * @param {boolean} fullyPaidByClient
 * @param {object} settings
 * @param {Array}  roster
 */
export function computeJobLedger(job, timeEntries = [], fullyPaidByClient = false, settings = {}, roster = []) {
  const out = [];
  const status = job?.status || '';
  const { originators, prod, post } = getJobAssignments(job);

  const findCrewId = (name) => {
    const lc = (name || '').toLowerCase();
    const m = roster.find(r => (r.name || '').toLowerCase() === lc);
    return m?.crewId || m?.crew_id || null;
  };

  // ── Originators ────────────────────────────────────────────────────────
  // Originator now fires on status='paid' alone (client-paid gate removed 2026-05-21).
  // The `fullyPaidByClient` parameter is retained for backward compatibility with callers.
  originators.forEach(o => {
    if (!o.name) return;
    const amount = parseFloat(o.earned) || 0;
    if (amount <= 0) return;
    out.push({
      person: o.name,
      crewId: findCrewId(o.name),
      role: 'originator',
      amount,
      isEligibleNow: status !== 'void' && isStatusAtOrPast(status, 'paid'),
    });
  });

  // ── Items mode (payMode:'items') — per-item allocation, live recompute ──
  // Same engine function the calculator uses, run against LIVE time entries
  // (estFallback:false — estimates never payable, never dock a lead), summed
  // per (person, role) to satisfy the uniq_jel_active (job_id, person, role)
  // index. Amounts are pre-expense, matching legacy ledger semantics.
  if (job?.calcSnapshot?.payMode === 'items') {
    const itemsRows = computeItemsLedgerRows(job, timeEntries, findCrewId);
    itemsRows.forEach(r => {
      out.push({ ...r, isEligibleNow: isStatusAtOrPast(status, ROLE_TRIGGERS[r.role]) && status !== 'void' });
    });
    return out;
  }

  // ── Helper: per-dept hourly aggregation from time entries ──────────────
  function deptHourly(dept, leadName) {
    const lcLead = (leadName || '').toLowerCase();
    // Filter entries belonging to this dept by category
    const relevant = timeEntries.filter(e => categoryToDept(e.category) === dept);

    // Aggregate hours per person
    const byPerson = {};
    relevant.forEach(e => {
      const name = e.person || '';
      if (!name) return;
      if (!byPerson[name]) byPerson[name] = { hours: 0 };
      byPerson[name].hours += parseFloat(e.hours) || 0;
    });

    // Compute earnings; lead's own hours zeroed (advisory only)
    const personEarnings = {};
    let nonLeadTotal = 0;
    Object.entries(byPerson).forEach(([name, data]) => {
      const isLead = lcLead && name.toLowerCase() === lcLead;
      const sampleCat = dept === 'prod' ? 'Shoot' : 'Edit';
      const rate = getRateForPersonOnJob(job, name, sampleCat, settings, roster);
      const amount = isLead ? 0 : data.hours * rate;
      personEarnings[name] = { hours: data.hours, rate, amount, isLead };
      if (!isLead) nonLeadTotal += amount;
    });

    return { personEarnings, nonLeadTotal };
  }

  // ── Production rows ────────────────────────────────────────────────────
  const prodTriggered = isStatusAtOrPast(status, ROLE_TRIGGERS.prod_lead) && status !== 'void';
  const prodResult = deptHourly('prod', prod.lead?.name);
  const prodLeadEarned = computeLeadRemainder(prod.budget, prodResult.nonLeadTotal);

  if (prod.lead?.name && prodLeadEarned > 0) {
    out.push({
      person: prod.lead.name,
      crewId: findCrewId(prod.lead.name),
      role: 'prod_lead',
      amount: prodLeadEarned,
      isEligibleNow: prodTriggered,
    });
  }
  Object.entries(prodResult.personEarnings).forEach(([name, d]) => {
    if (d.isLead || d.amount <= 0) return;
    out.push({
      person: name,
      crewId: findCrewId(name),
      role: 'prod_hourly',
      amount: d.amount,
      hours: d.hours,
      isEligibleNow: prodTriggered,
    });
  });

  // ── Post rows ──────────────────────────────────────────────────────────
  const postTriggered = isStatusAtOrPast(status, ROLE_TRIGGERS.post_lead) && status !== 'void';
  const postResult = deptHourly('post', post.lead?.name);
  const postLeadEarned = computeLeadRemainder(post.budget, postResult.nonLeadTotal);

  if (post.lead?.name && postLeadEarned > 0) {
    out.push({
      person: post.lead.name,
      crewId: findCrewId(post.lead.name),
      role: 'post_lead',
      amount: postLeadEarned,
      isEligibleNow: postTriggered,
    });
  }
  Object.entries(postResult.personEarnings).forEach(([name, d]) => {
    if (d.isLead || d.amount <= 0) return;
    out.push({
      person: name,
      crewId: findCrewId(name),
      role: 'post_hourly',
      amount: d.amount,
      hours: d.hours,
      isEligibleNow: postTriggered,
    });
  });

  return out;
}

// ── Items mode → ledger rows ──────────────────────────────────────────────

/**
 * Recompute per-item allocations from the snapshot's durable inputs (shares,
 * leads, hourly attachments + rates) against LIVE time entries, then sum to
 * one row per (person, role). Returns rows WITHOUT isEligibleNow (caller adds).
 * Optionally returns per-piece detail via the third element for prefill.
 */
function computeItemsLedgerRows(job, timeEntries, findCrewId, wantDetail = false) {
  const ns = job?.calcSnapshot || {};
  const allocs = ns.itemAllocs || [];

  const pieces = allocs.map(a => ({
    key: a.key, sid: a.sid, subId: a.subId ?? null,
    desc: a.desc || '', lineDesc: a.lineDesc || '',
    amount: parseFloat(a.amount) || 0,
  }));
  const assignments = {};
  allocs.forEach(a => {
    const member = (m) => ({
      rosterId: m.rosterId || null, name: m.name || '', crewId: m.crewId || null,
      hourlyRate: m.hourlyRate || 0, estHours: m.estHours || 0,
    });
    assignments[a.key] = {
      soloLead: !!a.soloLead,
      shootLead: a.shoot?.lead ? member(a.shoot.lead) : null,
      editLead: a.edit?.lead ? member(a.edit.lead) : null,
      shootHourly: (a.shoot?.hourly || []).map(member),
      editHourly: (a.edit?.hourly || []).map(member),
    };
  });

  const result = computeItemsAllocation({
    pieces,
    revenue: parseFloat(ns.totalRevenue) || parseFloat(job?.revenue) || 0,
    splits: ns.splits || {},
    assignments,
    timeEntries: timeEntries || [],
    estFallback: false,
  });

  const sums = {}; // person|role → row
  const add = (name, crewId, role, amount, hours) => {
    if (!name || !(amount > 0)) return;
    const k = `${name.toLowerCase()}|${role}`;
    if (!sums[k]) sums[k] = { person: name, crewId: crewId || findCrewId(name), role, amount: 0 };
    sums[k].amount = Math.round((sums[k].amount + amount) * 100) / 100;
    if (hours) sums[k].hours = Math.round(((sums[k].hours || 0) + hours) * 100) / 100;
  };

  result.itemAllocs.forEach(a => {
    [['shoot', 'prod'], ['edit', 'post']].forEach(([side, dept]) => {
      const s = a[side];
      if (s.lead?.name) add(s.lead.name, s.lead.crewId, `${dept}_lead`, s.leadEarned);
      (s.hourly || []).forEach(h => { add(h.name, h.crewId, `${dept}_hourly`, h.earned, h.hours); });
    });
  });

  const rows = Object.values(sums);
  return wantDetail ? { rows, result } : rows;
}

/**
 * Budgeted-but-unlogged hourly crew on an items job.
 *
 * The ledger pays hourly crew only for LOGGED time (estimates are never
 * payable). The side's lead earns `share − hourlyTotal`, so every unlogged
 * hour silently moves that crew member's budget into the lead's row. This
 * surfaces those gaps so payroll can flag them instead of quietly overpaying.
 *
 * Returns [] for legacy/staycation jobs (no per-item hourly attachments).
 *
 * @returns {Array<{item, side, person, unloggedHours, rate, amount, absorbedBy}>}
 *          `absorbedBy` = the lead who received the money, or null (→ Company).
 */
export function getUnloggedHourlyGaps(job, timeEntries = []) {
  if (job?.calcSnapshot?.payMode !== 'items') return [];
  const r2 = (n) => Math.round(n * 100) / 100;
  const { result } = computeItemsLedgerRows(job, timeEntries, () => null, true);
  const gaps = [];

  result.itemAllocs.forEach(a => {
    [['shoot', 'prod'], ['edit', 'post']].forEach(([side, dept]) => {
      const s = a[side];
      (s.hourly || []).forEach(h => {
        const rate = parseFloat(h.hourlyRate) || 0;
        const unloggedHours = r2((parseFloat(h.estHours) || 0) - (parseFloat(h.hours) || 0));
        if (unloggedHours <= 0 || rate <= 0) return;
        gaps.push({
          item: a.desc || '',
          side: dept,
          person: h.name || '',
          unloggedHours,
          rate,
          amount: r2(unloggedHours * rate),
          absorbedBy: s.lead?.name || null,
        });
      });
    });
  });

  return gaps;
}

// ── Admin pool earnings (not project-bound) ───────────────────────────────

/**
 * Aggregate admin-pool earnings (Meeting/Admin/Other) per person from time entries.
 * These are NOT project-bound — they're a company expense paid at matrix admin rate.
 *
 * @param {Array}  timeEntries — any time entries (will filter to admin-pool categories)
 * @param {object} settings
 * @param {Array}  roster
 * @returns {Array<{person, crewId, hours, amount, entries}>}
 */
export function computeAdminPoolEarnings(timeEntries = [], settings = {}, roster = [], jobsById = null) {
  const byPerson = {};
  timeEntries.forEach(e => {
    if (!isCategoryAdminPool(e.category)) return;
    // Skip entries tied to voided/archived jobs if a lookup was provided
    if (jobsById) {
      const jid = e.jobId || e.job_id;
      if (jid) {
        const j = jobsById[jid];
        if (j && (j.status === 'void' || j.status === 'archived')) return;
      }
    }
    const name = e.person || '';
    if (!name) return;
    if (!byPerson[name]) {
      const member = roster.find(r => (r.name || '').toLowerCase() === name.toLowerCase());
      byPerson[name] = {
        person: name,
        crewId: member?.crewId || member?.crew_id || null,
        hours: 0,
        amount: 0,
        entries: [],
      };
    }
    const hours = parseFloat(e.hours) || 0;
    const crewId = byPerson[name].crewId;
    const rate = parseFloat(settings?.data?.crewRates?.[crewId]?.admin) || 0;
    byPerson[name].hours += hours;
    byPerson[name].amount += hours * rate;
    byPerson[name].entries.push({ id: e.id, date: e.date, category: e.category, hours, amount: hours * rate });
  });
  return Object.values(byPerson);
}

// ── Mileage aggregation ───────────────────────────────────────────────────

/**
 * Aggregate mileage reimbursement per person from time entries.
 * Only entries with category='Travel' AND drove === true count.
 * If `jobsById` is provided, entries tied to voided/archived jobs are excluded.
 */
export function computeMileageEarnings(timeEntries = [], settings = {}, roster = [], jobsById = null) {
  const byPerson = {};
  const rate = getMileageRate(settings);
  timeEntries.forEach(e => {
    if (!isCategoryMileage(e.category)) return;
    if (e.drove === false) return; // explicit passenger
    // Skip entries tied to voided/archived jobs if a lookup was provided
    if (jobsById) {
      const jid = e.jobId || e.job_id;
      if (jid) {
        const j = jobsById[jid];
        if (j && (j.status === 'void' || j.status === 'archived')) return;
      }
    }
    const miles = parseFloat(e.miles) || 0;
    if (miles <= 0) return;
    const name = e.person || '';
    if (!name) return;
    if (!byPerson[name]) {
      const member = roster.find(r => (r.name || '').toLowerCase() === name.toLowerCase());
      byPerson[name] = {
        person: name,
        crewId: member?.crewId || member?.crew_id || null,
        miles: 0,
        amount: 0,
        entries: [],
      };
    }
    byPerson[name].miles += miles;
    byPerson[name].amount += miles * rate;
    byPerson[name].entries.push({ id: e.id, date: e.date, jobId: e.jobId, miles, amount: miles * rate });
  });
  return Object.values(byPerson);
}

// ── Crew-earnings grid: job → prefill suggestion ───────────────────────────

/**
 * Suggest crew-earnings line items for a person on a job (for the editable grid).
 * Uses computeJobLedger for the authoritative $ amounts (lead remainder, originator,
 * hourly) and the snapshot splits for a Sierra-style description. All editable after.
 *
 * Returns an ARRAY of rows, or null when the person has nothing earnable:
 *   - legacy jobs → one aggregate row (previous behavior, now wrapped)
 *   - items jobs (payMode:'items') → one row per item share, plus originator/hourly
 *
 * @returns {Array<{ item: string, amount: number, description: string }> | null}
 */
export function computePrefillForPersonOnJob(job, personName, jobTimeEntries = [], settings = {}, roster = []) {
  if (!job || !personName || !job.calcSnapshot) return null;
  const lc = personName.toLowerCase();
  const ns = job.calcSnapshot;
  const splits = ns.splits || {};
  const pct = (v) => (v != null && v !== '' ? `${v}% ` : '');
  const r2 = (n) => Math.round(n * 100) / 100;

  // ── Items mode: one row per item share ──────────────────────────────────
  if (ns.payMode === 'items') {
    const findCrewId = (name) => {
      const m = roster.find(r => (r.name || '').toLowerCase() === (name || '').toLowerCase());
      return m?.crewId || m?.crew_id || null;
    };
    const { result } = computeItemsLedgerRows(job, jobTimeEntries, findCrewId, true);
    const rows = [];

    const o = (ns.originators || []).find(x => (x.name || '').toLowerCase() === lc);
    const origEarned = parseFloat(o?.earned) || 0;
    if (origEarned > 0) {
      rows.push({ item: job.title || 'Untitled', amount: r2(origEarned), description: `${pct(o.pct ?? splits.orig)}Originator` });
    }

    result.itemAllocs.forEach(a => {
      const isShootLead = a.shoot.lead && (a.shoot.lead.name || '').toLowerCase() === lc;
      const isEditLead = a.edit.lead && (a.edit.lead.name || '').toLowerCase() === lc;
      if (isShootLead && isEditLead) {
        const amt = r2(a.shoot.leadEarned + a.edit.leadEarned);
        if (amt > 0) rows.push({ item: a.desc, amount: amt, description: 'Item Lead' });
      } else {
        if (isShootLead && a.shoot.leadEarned > 0) {
          rows.push({ item: a.desc, amount: r2(a.shoot.leadEarned), description: `${pct(splits.prod)}Prod Lead` });
        }
        if (isEditLead && a.edit.leadEarned > 0) {
          rows.push({ item: a.desc, amount: r2(a.edit.leadEarned), description: `${pct(splits.post)}Post Lead` });
        }
      }
      [['shoot', 'Shoot'], ['edit', 'Edit']].forEach(([side, label]) => {
        (a[side].hourly || []).forEach(h => {
          if ((h.name || '').toLowerCase() === lc && h.earned > 0) {
            rows.push({ item: a.desc, amount: r2(h.earned), description: h.hours ? `${h.hours}h ${label}` : label });
          }
        });
      });
    });

    return rows.length ? rows : null;
  }

  // ── Legacy: single aggregate row ────────────────────────────────────────
  const mine = computeJobLedger(job, jobTimeEntries, false, settings, roster)
    .filter(r => (r.person || '').toLowerCase() === lc);
  if (!mine.length) return null;
  const amount = r2(mine.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0));
  if (amount <= 0) return null;

  const has = (role) => mine.some(r => r.role === role);
  const parts = [];
  if (has('originator')) {
    const o = (ns.originators || []).find(x => (x.name || '').toLowerCase() === lc);
    parts.push(`${pct(o?.pct ?? splits.orig)}Originator`);
  }
  const pl = has('prod_lead'), sl = has('post_lead');
  if (pl && sl) {
    parts.push(splits.prod === splits.post
      ? `${pct(splits.prod)}Prod & Post Lead`
      : `${pct(splits.prod)}Prod & ${pct(splits.post)}Post Lead`);
  } else if (pl) parts.push(`${pct(splits.prod)}Prod Lead`);
  else if (sl) parts.push(`${pct(splits.post)}Post Lead`);
  mine.filter(r => r.role === 'prod_hourly' || r.role === 'post_hourly').forEach(r => {
    const side = r.role === 'prod_hourly' ? 'Prod' : 'Post';
    parts.push(r.hours ? `${r.hours}h ${side}` : side);
  });

  return [{ item: job.title || 'Untitled', amount, description: parts.filter(Boolean).join(' + ') }];
}
